import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { WarningRegular } from '@fluentui/react-icons';
import { Shell, ContactFooter } from '../components/Shell';
import { IconCircle, Lead, Muted, Title } from '../components/ui';
import { consentStartUrl, useSignup, validSignupId } from '../lib/signupState';

// /api/consent/callback redirects here with ?reason=…&id=… on failure.
const reasons: Record<string, string> = {
  declined: 'The Microsoft consent screen was cancelled or declined. Nothing was changed in your tenant.',
  not_admin:
    'Approving TELLEC Docs needs a Microsoft 365 Global Admin. Send them the link and they can finish it in a minute.',
  expired: 'That consent link has expired or was already used. Start again and it only takes a minute.',
  failed: 'Something went wrong on the way back from Microsoft. Nothing was changed in your tenant.',
};

const useStyles = makeStyles({
  actions: { display: 'grid', gap: '12px' },
  button: { minHeight: '48px', fontSize: tokens.fontSizeBase400 },
});

export function ConsentError() {
  const s = useStyles();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { state } = useSignup();
  const reason = params.get('reason') ?? 'failed';
  const signupId = validSignupId(params.get('id')) || validSignupId(state.signupId);
  const canRetry = Boolean(signupId) && reason !== 'expired';

  const retry = (
    <Button
      key="retry"
      size="large"
      className={s.button}
      appearance={reason === 'not_admin' ? 'secondary' : 'primary'}
      onClick={() => (canRetry ? window.location.assign(consentStartUrl(signupId)) : navigate('/details'))}
    >
      {canRetry ? 'Try again' : 'Start again'}
    </Button>
  );
  const handoff = canRetry ? (
    <Button
      key="handoff"
      size="large"
      className={s.button}
      appearance={reason === 'not_admin' ? 'primary' : 'secondary'}
      onClick={() => navigate(`/done?status=admin&id=${encodeURIComponent(signupId)}`)}
    >
      Send the link to a Global Admin
    </Button>
  ) : null;

  return (
    <Shell
      labelledBy="title"
      brandTitle="Nearly there"
      brandTagline="Your terms are saved. Only the Microsoft step needs another go."
      footerLeft={<ContactFooter />}
    >
      <IconCircle tone="warn">
        <WarningRegular />
      </IconCircle>
      <Title id="title">Microsoft consent wasn't completed</Title>
      <Lead>{reasons[reason] ?? reasons.failed}</Lead>
      {/* Lead with the hand-off when the problem is permissions. */}
      <div className={s.actions}>{reason === 'not_admin' ? [handoff, retry] : [retry, handoff]}</div>
      <Muted>We've let the TELLEC team know, so someone may be in touch to help.</Muted>
    </Shell>
  );
}
